import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CollectionFanPreview } from '@/components/ui/collection-fan-preview';
import { Badge } from '@/components/ui/badge';
import { Plus, Sparkles } from 'lucide-react';
import { PRICES } from '@/utils/cart';
import { useTranslation } from 'react-i18next';

interface UpsellOpportunity {
  participantId: string;
  participantName: string;
  raceId: string;
  raceName: string;
  eventId: string;
  currentPhotos: number;
  currentCost: number;
  collectionCost: number;
  delta: number;
  photoIds: number[];
  isGroup?: boolean;
  isWinner?: boolean;
}

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunities: UpsellOpportunity[];
  onUpgradeAndProceed: (participantIds: string[]) => void;
  onDecline: () => void;
}

export const UpsellModal: React.FC<UpsellModalProps> = ({
  isOpen,
  onClose,
  opportunities,
  onUpgradeAndProceed,
  onDecline,
}) => {
  const { t } = useTranslation();
  const [selectedUpgrades, setSelectedUpgrades] = React.useState<Set<string>>(new Set());

  // Fallback: Filter out winner and group photos from opportunities
  const filteredOpportunities = React.useMemo(() => 
    opportunities.filter(opp => !opp.isGroup && !opp.isWinner),
    [opportunities]
  );

  // Auto-select if only one opportunity
  React.useEffect(() => {
    if (filteredOpportunities.length === 1) {
      setSelectedUpgrades(new Set([filteredOpportunities[0].participantId]));
    } else {
      setSelectedUpgrades(new Set());
    }
  }, [filteredOpportunities]);

  React.useEffect(() => {
    console.log('UpsellModal render - isOpen:', isOpen, 'opportunities:', opportunities.length, 'filtered:', filteredOpportunities.length);
  }, [isOpen, opportunities, filteredOpportunities]);

  const toggleUpgrade = (participantId: string) => {
    const newSelected = new Set(selectedUpgrades);
    if (newSelected.has(participantId)) {
      newSelected.delete(participantId);
    } else {
      newSelected.add(participantId);
    }
    setSelectedUpgrades(newSelected);
  };

  const handleUpgrade = () => {
    if (selectedUpgrades.size === 0 && filteredOpportunities.length > 1) {
      return;
    }
    
    onUpgradeAndProceed(Array.from(selectedUpgrades));
  };

  const getUpgradeButtonText = () => {
    if (filteredOpportunities.length === 1) {
      return t('cart.upsellModal.upgradeAndCheckout');
    }
    if (selectedUpgrades.size === 0) {
      return t('cart.upsellModal.selectAtLeastOne');
    }
    return t('cart.upsellModal.upgradeMultipleAndCheckout', { count: selectedUpgrades.size });
  };

  const totalSavings = filteredOpportunities
    .filter(opp => selectedUpgrades.has(opp.participantId) && opp.delta < 0)
    .reduce((sum, opp) => sum + Math.abs(opp.delta), 0);

  const totalAdditionalCost = filteredOpportunities
    .filter(opp => selectedUpgrades.has(opp.participantId) && opp.delta > 0)
    .reduce((sum, opp) => sum + opp.delta, 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto bg-background p-4 sm:p-6 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-2xl">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
            <span className="line-clamp-2">{t('cart.upsellModal.title')}</span>
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {t('cart.upsellModal.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 mt-4">
          {filteredOpportunities.map((opp) => (
            <div
              key={`${opp.participantId}-${opp.raceId}`}
              className="border rounded-xl p-3 sm:p-4 space-y-3 hover:border-primary/50 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex-shrink-0">
                      <CollectionFanPreview
                        eventId={opp.eventId}
                        raceId={opp.raceId}
                        participantName={opp.participantName}
                        participantId={opp.participantId}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base sm:text-lg truncate">{opp.participantName}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{opp.raceName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs">
                      {t('cart.upsellModal.individualPhotos', { count: opp.currentPhotos })}
                    </Badge>
                    <span className="text-muted-foreground text-xs sm:text-sm">→</span>
                    <Badge variant="default" className="bg-primary text-white text-xs">
                      {t('cart.upsellModal.fullCollectionLabel')}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                    <span className="text-muted-foreground line-through">
                      CHF {opp.currentCost.toFixed(2)}
                    </span>
                    <span className="font-semibold text-base sm:text-lg text-primary">
                      CHF {opp.collectionCost.toFixed(2)}
                    </span>
                    {opp.delta < 0 ? (
                      <Badge variant="destructive" className="bg-green-600 text-white text-xs">
                        {t('cart.upsellModal.saveBadge', { amount: Math.abs(opp.delta).toFixed(2) })}
                      </Badge>
                    ) : opp.delta > 0 ? (
                      <Badge variant="secondary" className="text-xs">
                        {t('cart.upsellModal.addCost', { amount: opp.delta.toFixed(2) })}
                      </Badge>
                    ) : null}
                  </div>
                </div>

                {filteredOpportunities.length > 1 && (
                  <Button
                    variant={selectedUpgrades.has(opp.participantId) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleUpgrade(opp.participantId)}
                    className="shrink-0 w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {selectedUpgrades.has(opp.participantId) ? t('cart.upsellModal.upgradeSelected') : t('cart.upsellModal.upgrade')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={onDecline} className="w-full sm:w-auto text-sm">
              {t('cart.upsellModal.noThanksCheckout')}
            </Button>
            <Button 
              onClick={handleUpgrade}
              disabled={filteredOpportunities.length > 1 && selectedUpgrades.size === 0}
              className="w-full sm:w-auto text-sm"
            >
              {getUpgradeButtonText()}
            </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
